/* Handmatig een lead aanmaken (bron: manual / groen). */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Plus, X, Loader2, User as UserIcon } from "lucide-react";
import { FieldX } from "./FieldX";

/* ------------------------------------------------------------------ */
/*  Quick lead create modal (manual entry — green)                     */
/* ------------------------------------------------------------------ */
function LeadCreateModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        company_name: "", contact_name: "", contact_title: "",
        contact_email: "", notes: "", score: 60, stage: "new", manual_source: "", deal_value: "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name.trim()) { toast.error(i18n.t("pipeline_sales.company_required")); return; }
        setSaving(true);
        try {
            await api.post("/leads/manual", {
                ...form,
                score: Number(form.score) || 60,
                deal_value: form.deal_value !== "" ? Math.max(0, Number(form.deal_value) || 0) : null,
            });
            toast.success(i18n.t("pipeline_sales.lead_added", { company: form.company_name }));
            onCreated?.();
        } catch (err) { toast.error(err?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} data-testid="lead-create-modal"
                className="w-full max-w-lg bg-[var(--jr-surface)] border border-[var(--jr-success)]/40 rounded-2xl p-6 jr-fadein max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl tracking-tight inline-flex items-center gap-2">
                            <UserIcon className="text-[var(--jr-success)]" size={18} /> {i18n.t("pipeline_sales.new_lead")}
                        </h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{i18n.t("pipeline_sales.new_lead_sub_pre")} <span className="text-[var(--jr-success)]">{i18n.t("pipeline_sales.green")}</span> {i18n.t("pipeline_sales.new_lead_sub_post")}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="lc-close" className="text-[var(--jr-text-3)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <FieldX label="Company *" testid="lc-company">
                        <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                    <FieldX label="Source" testid="lc-source">
                        <select value={form.manual_source} onChange={(e) => setForm({ ...form, manual_source: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="">—</option><option>Referral</option><option>Conference</option><option>Inbound</option><option>LinkedIn</option><option>Outbound</option>
                        </select>
                    </FieldX>
                    <FieldX label="Contact" testid="lc-contact">
                        <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                    <FieldX label="Functie" testid="lc-title">
                        <input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                    <FieldX label="Email" testid="lc-email">
                        <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                    <FieldX label="Stage" testid="lc-stage">
                        <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="new">new</option><option value="contacted">contacted</option><option value="engaged">engaged</option>
                            <option value="meeting">meeting</option><option value="won">won</option><option value="lost">lost</option>
                        </select>
                    </FieldX>
                    <FieldX label={i18n.t("pipeline_sales.deal_value")} testid="lc-deal-value">
                        <input type="number" min="0" step="100" value={form.deal_value}
                            onChange={(e) => setForm({ ...form, deal_value: e.target.value })}
                            placeholder={i18n.t("pipeline_sales.deal_value_ph")}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                    <FieldX label="Notities" testid="lc-notes" full>
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                    </FieldX>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} data-testid="lc-cancel"
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={saving} data-testid="lc-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-success)] text-black text-sm font-medium hover:opacity-90 disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} {i18n.t("pipeline_sales.make_lead")}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default LeadCreateModal;
